import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import { InputLabel } from "@material-ui/core";
import dayjs, { Dayjs } from "dayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { selectHeatMapDateRange } from "../../store/heatmap/HeatMapSelectors";
import { setHeatMapDateRange } from "../../store/heatmap/HeatMapActions";
import { setPrefetchPauseUntil } from "../../store/product-list/ProductListActions";
import {
  HEAT_MAP_DATE_RANGE_LABELS,
  HEAT_MAP_DATE_RANGE_OPTIONS,
  HeatMapDateRangeOption,
} from "../../utils/HeatMapUtils";
import {
  StyledConfigSection,
  StyledHeatMapFormControl,
  StyledHeatMapSelect,
  heatMapModalStyles,
} from "./HeatMapModalStyles";

/** Format used by the heatmap date range slice for custom date strings. */
const PICKER_FORMAT = "YYYY-MM-DD";

/**
 * Date range picker — predefined dropdown plus DesktopDatePicker controls
 * for "Specific Date" and "Date Range".
 *
 * Uses `@mui/x-date-pickers` (DesktopDatePicker + LocalizationProvider +
 * AdapterDayjs) to match the rest of SM4 (PreviewPopup, AutomationRules).
 */
const HeatMapDateRangePicker: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const styles = heatMapModalStyles();
  const dateRange = useSelector(selectHeatMapDateRange);
  const labelId = "heatmap-date-range-label";

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newOption = event.target.value as HeatMapDateRangeOption;
    // SM4A-2618 — pause prefetch so date-range change commits on a free thread.
    dispatch(setPrefetchPauseUntil(Date.now() + 500));
    if (
      newOption === HEAT_MAP_DATE_RANGE_OPTIONS.SPECIFIC_DATE ||
      newOption === HEAT_MAP_DATE_RANGE_OPTIONS.DATE_RANGE
    ) {
      dispatch(
        setHeatMapDateRange({
          option: newOption,
          customStartDate: dateRange.customStartDate,
          customEndDate: dateRange.customEndDate,
        }),
      );
    } else {
      dispatch(setHeatMapDateRange({ option: newOption }));
    }
  };

  const handleStartChange = (value: Dayjs | null) => {
    dispatch(setPrefetchPauseUntil(Date.now() + 500));
    dispatch(
      setHeatMapDateRange({
        ...dateRange,
        customStartDate: value ? value.format(PICKER_FORMAT) : undefined,
      }),
    );
  };

  const handleEndChange = (value: Dayjs | null) => {
    dispatch(setPrefetchPauseUntil(Date.now() + 500));
    dispatch(
      setHeatMapDateRange({
        ...dateRange,
        customEndDate: value ? value.format(PICKER_FORMAT) : undefined,
      }),
    );
  };

  const isSpecific =
    dateRange.option === HEAT_MAP_DATE_RANGE_OPTIONS.SPECIFIC_DATE;
  const isCustomRange =
    dateRange.option === HEAT_MAP_DATE_RANGE_OPTIONS.DATE_RANGE;

  const dropdownLabel = intl.formatMessage({
    id: "heatMap.dateRangeLabel",
    defaultMessage: "Select Date Range",
  });
  const specificDateLabel = intl.formatMessage({
    id: "heatMap.specificDateLabel",
    defaultMessage: "Date",
  });
  const startDateLabel = intl.formatMessage({
    id: "heatMap.startDateLabel",
    defaultMessage: "Start Date",
  });
  const endDateLabel = intl.formatMessage({
    id: "heatMap.endDateLabel",
    defaultMessage: "End Date",
  });

  // Today's date as a Dayjs instance — used to clamp future-date selection.
  const today = dayjs();
  const startValue = dateRange.customStartDate
    ? dayjs(dateRange.customStartDate)
    : null;
  const endValue = dateRange.customEndDate
    ? dayjs(dateRange.customEndDate)
    : null;

  return (
    <StyledConfigSection>
      <StyledHeatMapFormControl size="small" variant="outlined">
        <InputLabel id={labelId}>{dropdownLabel}</InputLabel>
        <StyledHeatMapSelect
          native
          variant="outlined"
          labelId={labelId}
          label={dropdownLabel}
          value={dateRange.option}
          onChange={handleChange}
          inputProps={{ "aria-label": "Heat Map Date Range" }}
        >
          {HEAT_MAP_DATE_RANGE_LABELS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </StyledHeatMapSelect>
      </StyledHeatMapFormControl>

      {isSpecific && (
        <Box sx={styles.dateRangePicker}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label={specificDateLabel}
              value={startValue}
              onChange={handleStartChange}
              maxDate={today}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      )}

      {isCustomRange && (
        <Box sx={styles.dateRangePicker}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label={startDateLabel}
              value={startValue}
              onChange={handleStartChange}
              maxDate={endValue ?? today}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
            <DesktopDatePicker
              label={endDateLabel}
              value={endValue}
              onChange={handleEndChange}
              minDate={startValue ?? undefined}
              maxDate={today}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      )}
    </StyledConfigSection>
  );
};

export default HeatMapDateRangePicker;
